import type { LocationGroup } from '@/models/account-management/LocationGroup';
import {
    type PlaylistSchedule, PlaylistScheduleScope, RecurrenceFrequency, WEEKDAY_BITS, hasDay,
} from './PlaylistSchedule';

// Turning recurrence RULES into the days they land on, for the calendar view.
//
// A schedule is not an event: every one repeats, and the stored form is a rule plus bounds. Drawing a
// calendar means asking each rule, for each visible day, "do you happen here".
//
// Dates here are CALENDAR DAYS, and the Date objects below exist only to step and compare them - built at
// local midnight, read back through getFullYear/getMonth/getDate, and never formatted through toISOString,
// which would shift them into UTC and slide a day either way for half the world. The times on a schedule are
// wall-clock and stay strings and minute counts; nothing in this file touches them.

export const toYmd = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Local midnight, so day arithmetic stays in the same day. `new Date("2026-08-15")` would parse as UTC.
export const fromYmd = (s: string): Date => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
};

export const addDays = (d: Date, n: number): Date =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

const daysBetween = (from: Date, to: Date): number =>
    // Both are local midnight, so this is whole days apart from DST shifts - hence the rounding, which turns
    // a 23- or 25-hour day back into 1.
    Math.round((to.getTime() - from.getTime()) / 86_400_000);

// Does this schedule run on this calendar day?
//
// Only ever a question about the DAY. Whether it is currently within its window, and which playlist wins when
// several are, are different questions - the second one the server answers, since it needs the location's
// timezone and day parts.
export const occursOn = (s: PlaylistSchedule, day: Date): boolean => {
    const ymd = toYmd(day);

    if (s.startDate && ymd < s.startDate) return false;
    if (s.recurrenceEndDate && ymd > s.recurrenceEndDate) return false;

    // The anchor for every "every Nth" test. Without a start date there is nothing to count from, so the
    // interval is treated as 1 rather than guessed at.
    const start = s.startDate ? fromYmd(s.startDate) : null;
    const interval = Math.max(1, s.recurrenceInterval ?? 1);

    switch (s.recurrenceFrequency) {
        case RecurrenceFrequency.Weekly: {
            // No days chosen means the rule cannot say which day it wants, so it is treated as landing on
            // every day of a matching week rather than on none - a schedule that exists should be visible.
            const anyDay = WEEKDAY_BITS.some((bit) => hasDay(s.recurrenceDaysOfWeek, bit));
            if (anyDay && !hasDay(s.recurrenceDaysOfWeek, WEEKDAY_BITS[day.getDay()])) return false;
            if (!start || interval === 1) return true;
            // Weeks counted from the start date itself, so a fortnightly schedule repeats on the same
            // rhythm it was created on.
            return Math.floor(daysBetween(start, day) / 7) % interval === 0;
        }

        case RecurrenceFrequency.Monthly: {
            const dayOfMonth = s.recurrenceDayOfMonth ?? start?.getDate();
            if (dayOfMonth && day.getDate() !== dayOfMonth) return false;
            if (!start || interval === 1) return true;
            const months = (day.getFullYear() - start.getFullYear()) * 12 + (day.getMonth() - start.getMonth());
            return months % interval === 0;
        }

        // Daily, and anything unset - a schedule with no frequency still runs, every day.
        default:
            if (!start || interval === 1) return true;
            return daysBetween(start, day) % interval === 0;
    }
};

// Does this schedule reach this location?
//
// Brand targets reach everything; a group target reaches its members; a location target reaches itself. This
// is about VISIBILITY, not precedence - a schedule that reaches a location may still lose to a more specific
// one, which only the server can work out.
export const reachesLocation = (
    s: PlaylistSchedule,
    locationId: string,
    groups: LocationGroup[],
): boolean =>
    s.targets.some((t) => {
        if (t.scope === PlaylistScheduleScope.Brand) return true;
        if (t.scope === PlaylistScheduleScope.Location) return t.targetId === locationId;
        return !!groups
            .find((g) => g.id === t.targetId)
            ?.members.some((m) => m.locationId === locationId);
    });

export interface ResolvedWindow {
    startMinute: number;
    endMinute: number;
}

// The hours a schedule occupies, for the week and day grids. Null = all day, which is a real choice rather
// than missing times and belongs in the all-day row instead of the grid.
//
// A day part is passed in ALREADY LOOKED UP, because its hours are a fact about a location, not about the
// schedule - this file has no business fetching one. A schedule pointing at a day part that no longer exists
// also returns null: it will not run at all, and the caller labels it rather than drawing hours it does not
// have.
export const resolveWindow = (
    s: Pick<PlaylistSchedule, 'dayPartId' | 'startMinute' | 'endMinute'>,
    dayPart?: { startMinute: number; endMinute: number } | null,
): ResolvedWindow | null => {
    if (s.dayPartId) {
        return dayPart ? { startMinute: dayPart.startMinute, endMinute: dayPart.endMinute } : null;
    }
    if (s.startMinute == null) return null;

    const endMinute = s.endMinute ?? 1440;
    // Equal ends means the window covers the whole day rather than nothing.
    return s.startMinute === endMinute ? null : { startMinute: s.startMinute, endMinute };
};

// What to draw on ONE day for a window, split where it crosses midnight.
//
// `endMinute < startMinute` means the window wraps - 22:00-02:00 is 1320 -> 120 - so it draws as a block
// running to the bottom of its own day and another from the top of the next. `tail` is that second piece,
// and belongs to the day AFTER the one the schedule occurs on.
export const windowPieces = (w: ResolvedWindow): { head: ResolvedWindow; tail?: ResolvedWindow } =>
    w.endMinute > w.startMinute
        ? { head: w }
        : {
            head: { startMinute: w.startMinute, endMinute: 1440 },
            tail: { startMinute: 0, endMinute: w.endMinute },
        };

// The six weeks a month grid draws: whole weeks, Sunday-first, covering every day of the month.
export const monthGridDays = (year: number, month: number): Date[] => {
    const first = new Date(year, month, 1);
    const startOfGrid = addDays(first, -first.getDay());
    return Array.from({ length: 42 }, (_, i) => addDays(startOfGrid, i));
};

// The seven days of the week containing `day`, Sunday-first, to match the month grid.
export const weekDays = (day: Date): Date[] => {
    const sunday = addDays(day, -day.getDay());
    return Array.from({ length: 7 }, (_, i) => addDays(sunday, i));
};

// Side-by-side lanes for blocks that overlap in time, so a collision is visible as two columns rather than
// one block hidden behind another - which is the entire reason to draw hours instead of a list.
//
// Returns each block's lane and how many lanes the day needs. A block reuses the first lane whose previous
// block has already ended, so a day of non-overlapping blocks stays one lane wide.
export const assignLanes = <T extends { startMinute: number; endMinute: number }>(
    blocks: T[],
): { laid: Array<T & { lane: number }>; laneCount: number } => {
    const sorted = [...blocks].sort((a, b) => a.startMinute - b.startMinute || a.endMinute - b.endMinute);
    const laneEnds: number[] = [];

    const laid = sorted.map((b) => {
        let lane = laneEnds.findIndex((end) => end <= b.startMinute);
        if (lane === -1) lane = laneEnds.length;
        laneEnds[lane] = b.endMinute;
        return { ...b, lane };
    });

    return { laid, laneCount: Math.max(1, laneEnds.length) };
};
